<template>
  <VDialogV3
    position="center"
    width="1200px"
    :loading="false"
    title="Импорт"
  >
    <section>
      <template v-if="!showBuyersCode">
        <VInput
          placeholder="Ctrl + V сюда выделенный диапазон (вставьте список позиций)"
          @paste="onImport($event)"
        />

        <div v-if="importErrorsText">
          {{ importErrorsText }}
        </div>
      </template>

      <table v-if="!showBuyersCode && importedRawData.length > 0">
        <thead>
        <tr>
          <th
            v-for="(_select, index) of importedRawData[0]"
            :key="'order_select_' + index"
          >
            <VSelect
              v-model="importColumnOrder[index]"
              :options="importColumnList"
              @update:model-value="onImportColumnOrderChange($event, index)"
            />
          </th>
        </tr>
        </thead>

        <tbody>
        <tr
          v-for="(row, index) in importedRawData"
          :key="'row_' + index"
        >
          <td
            v-for="(column, cIndex) of importedRawData[index]"
            :key="'row_column_' + cIndex"
          >
            {{ column ? column : '--' }}
          </td>
        </tr>
        </tbody>
      </table>
    </section>

    <hr>

    <VDataGridV2
      v-if="showBuyersCode"
      :data="indexedPreviewTable"
      sticky
    >
      <VDataGridColumnV2
        id="index"
        label="#"
        width="40px"
        content-align="center"
      >
        <template #default="{ row }">
          {{ row.index + 1 }}
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="filter"
        label="Артикул"
        width="100px"
      >
        <template #default="{ row }">
          {{ row.data.buyersCode }}
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="name"
        label="Наименование"
        width="300px"
      >
        <template #default="{ row }">
          {{ row.data.name }}
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="qty"
        label="Количество"
        width="100px"
        content-class="right"
      >
        <template #default="{ row }">
          <VInputNumberRusV2
            v-model="row.data.qty"
            :type="Qty"/>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        v-if="isPreorder"
        id="preorderMonthlyReserve"
        label="Страх. запас, кол-во"
        width="100px"
        content-class="right"
      >
        <template #default="{ row }">
          <VInputNumberRusV2
            v-model="row.data.preorderMonthlyReserve"
            :type="Qty"/>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="filter-info"
        label="Товар"
        width="200px"
      >
        <template #default="{ row }">
          <template v-if="row.meta && row.meta.positionIsFound">
            <b>ID: {{ row.data.product.id }} </b>
            <span>{{ row.data.product.name }}</span>
          </template>
          <template v-else>
            <span class="warning">Товар по артикулу не найден</span>
          </template>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="action"
        width="52px"
        content-class="form"
      >
        <template #default="{ row }">
          <VButton
            title="Удалить"
            @click="deleteRow(row.index)"
          >
            <template #icon>
              <VIcon material="delete"/>
            </template>
          </VButton>
        </template>
      </VDataGridColumnV2>
    </VDataGridV2>

    <template #footer>
      <template v-if="!hasPreviewTable">
        <VButton
          theme="primary"
          :disabled="!canApproveImport"
          @click="approveImport"
        >
          Применить
        </VButton>
      </template>
      <template v-else>
        <VButton
          theme="primary"
          :disabled="importDisabled"
          @click="submit"
        >
          Сохранить
        </VButton>

        <span v-if="importDisabled">
          <VIcon material="warning_amber"/>
          {{ qtyUnresolvedPositions }}
          {{ $core.utils.regional.ru.pluralize(qtyUnresolvedPositions, 'позиция', 'позиции', 'позиций') }}
          не найдено
        </span>

        <VButton
          v-if="importDisabled"
          @click="deleteNotFoundPositions"
        >
          Удалить не найденные позиции
        </VButton>

        <VButton @click="close">
          Назад
        </VButton>
      </template>
    </template>
  </VDialogV3>
</template>

<script lang="ts">

import Core                    from '@/core/Core';
import DemandsService          from '@/domains/Demand/DemandsService';
import ClipboardXlsParserUtils from '@/core/utils/ClipboardXlsParserUtils';
import Qty                     from '@/core/types/Qty';

const BASE_IMPORT_COLUMNS = [
  { id: 'buyersCode', name: 'Артикул' },
  { id: 'name',       name: 'Наименование' },
  { id: 'qty',        name: 'Количество' },
];

const PREORDER_COLUMN = { id: 'preorderMonthlyReserve', name: 'Страх. запас, кол-во' };

export default defineComponent({
  name: 'DemandImportDialog',

  props: {
    isPreorder: {
      type:    Boolean,
      default: false,
    },
  },

  emits: ['close', 'onImport'],

  setup() {
    return {
      Qty
    };
  },

  data() {
    // Список колонок для VSelect и стартовая раскладка importColumnOrder.
    // Для предзаказа добавляется четвёртая колонка — страховой запас.
    const importColumnOrder: Array<{ id: string; name: string } | null> = [
      null,
      ...BASE_IMPORT_COLUMNS,
    ];
    const importColumnList = [...BASE_IMPORT_COLUMNS];

    if (this.isPreorder) {
      importColumnOrder.push(PREORDER_COLUMN);
      importColumnList.push(PREORDER_COLUMN);
    }

    return {
      importColumnOrder,
      importColumnList,

      importedRawData: [] as any[][],
      previewTable:    null as any[] | null,
      importHelper:    null as ClipboardXlsParserUtils | null,
      showBuyersCode:  false,

      form: [] as any[],
    };
  },

  computed: {
    /**
     * previewTable с проставленным index. VDataGridV2 не передаёт index в slot,
     * подкладываем его руками — для колонки "#" и для deleteRow.
     */
    indexedPreviewTable(): any[] {
      if (!Array.isArray(this.previewTable)) return [];
      return this.previewTable.map((item, index) => ({ ...item, index }));
    },

    hasPreviewTable(): boolean {
      return this.$core.utils.isNotEmptyArray(this.previewTable);
    },

    qtyUnresolvedPositions(): number | null {
      if (!this.hasPreviewTable) return null;
      return this.previewTable!.filter(({ meta }: any) => !meta?.positionIsFound).length;
    },

    canApproveImport(): boolean {
      if (this.importedRawData.length === 0) return false;
      const codeIndex = this.importColumnOrder.findIndex(
        item => item && item.id === 'buyersCode',
      );
      return codeIndex > -1;
    },

    /**
     * Импорт заблокирован — среди позиций есть ненайденные товары.
     * Пока true, кнопка «Сохранить» задизейблена.
     */
    importDisabled(): boolean {
      if (this.qtyUnresolvedPositions === null) {
        return false;
      }
      else {
        return this.qtyUnresolvedPositions > 0;
      }
    },

    importErrorsText(): string | null {
      if (this.importedRawData.length === 0) return null;

      const codeIndex = this.importColumnOrder.findIndex(
        item => item !== null && item.id === 'buyersCode',
      );

      if (codeIndex > -1) {
        return 'Можно импортировать данные';
      }
      else {
        return 'Нельзя импортировать данные';
      }
    },
  },

  methods: {
    /**
     * Финализировать импорт — отдать родителю позиции и закрыть диалог
     */
    submit(): void {
      this.$emit('onImport', this.previewTable!.map((item: any) => item.data));
      this.close();
    },

    /**
     * Парсит вставленный из буфера обмена диапазон в строки импорта
     */
    onImport($event: ClipboardEvent): void {
      if (!($event instanceof ClipboardEvent)) return;

      this.importHelper = new ClipboardXlsParserUtils($event);
      if (this.importHelper.rows.length > 0) {
        this.importedRawData = this.importHelper.rows;
      }
    },

    /**
     * Меняет назначение колонки при импорте — гарантирует, что одна и та же
     * сущность не назначена в двух колонках
     */
    onImportColumnOrderChange(value: any, position: number): void {
      const importColumnOrder = Core.utils.cloneDeep(this.importColumnOrder);

      if (value === null) {
        importColumnOrder[position] = null;
      } else {
        const index = importColumnOrder.findIndex(item => item && item.id === value.id);
        if (index !== -1) {
          importColumnOrder[index] = null;
        }
        importColumnOrder[position] = value;
      }
      this.importColumnOrder = importColumnOrder;
    },

    /**
     * Удалить строку превью по индексу
     */
    deleteRow(index: number): void {
      if (!Array.isArray(this.previewTable)) return;

      const previewTable = [...this.previewTable];
      previewTable.splice(index, 1);
      this.previewTable = previewTable;
    },

    /**
     * Удалить из превью все строки, для которых не нашлось товара
     */
    deleteNotFoundPositions(): void {
      if (!Array.isArray(this.previewTable)) return;
      this.previewTable = this.previewTable.filter((pos: any) => pos?.meta?.positionIsFound);
    },

    /**
     * Принять импорт: парсит сырые данные по выбранным колонкам, грузит товары
     * по списку артикулов и формирует превью-таблицу с подсветкой найденных
     */
    async approveImport(): Promise<void> {
      let form = this.importedRawData.map(currentColumn => {
        const result: Record<string, any> = {};
        this.importColumnOrder.forEach((columnOrder, index) => {
          const currentCell = currentColumn[index];
          if (columnOrder && currentCell) {
            result[columnOrder.id] = currentCell;
          }
        });
        return result;
      });

      form = form.map(item => {
        const qtyParsed = Qty.fromServer(parseFloat(
          String(item.qty).replaceAll(/\s/g, '').replaceAll(',', '.'),
        ));

        if (this.isPreorder) {
          const preorderMonthlyReserve = Qty.formServer(parseFloat(
            String(item.preorderMonthlyReserve).replaceAll(/\s/g, '').replaceAll(',', '.'),
          ));

          if (isNaN(preorderMonthlyReserve)) {
            item.preorderMonthlyReserve = null;
          }
          else {
            item.preorderMonthlyReserve = preorderMonthlyReserve;
          }
        }

        if (qtyParsed.isEmpty()) {
          item.qty = null;
        }
        else {
          item.qty = qtyParsed;
        }

        if (item.buyersCode && item.buyersCode.length > 0) {
          item.buyersCode = item.buyersCode.replaceAll(/\s/g, '');
        }
        else {
          item.buyersCode = null;
        }

        return item;
      });

      this.form              = form;
      this.importedRawData   = [];
      this.importColumnOrder = [];

      const buyersCode = this.form.reduce<string[]>((acc, current) => {
        if (current?.buyersCode) acc.push(current.buyersCode);
        return acc;
      }, []);

      const products = await DemandsService.findProductsByCodes(buyersCode);

      this.previewTable = this.form.map((pos: any) => {
        const result: any = {
          data: pos,
          meta: { positionIsFound: false },
        };

        const currentFilter = products.find((filter: any) => filter.productcode === pos.buyersCode);
        if (currentFilter) {
          result.data.product = {
            id:                  currentFilter.productid,
            code:                currentFilter.productcode,
            name:                currentFilter.productname,
            productCategoryName: currentFilter.productname,
          };
          result.meta.positionIsFound = true;
        }
        return result;
      });

      this.showBuyersCode = true;
    },

    close(): void {
      this.$core.dialogs.DemandImportDialog.hide();
    },
  },
});
</script>
